import { statusColors } from '@parsina/framework/tokens';
import { isoDayOf, wallToMs } from '../lib/clinicZone';

/**
 * Where a clinical order stands, as the desk names it — and the day it is to be
 * done (PM-0634, docs/ORDER_DATE_AND_STAGE_PLAN.md §2).
 *
 * The STAGE is the BFF's. It is derived on read (`src/lib/orderStage.js` in the
 * clinic BFF — the same rule in the doctor and patient BFFs) from the stored
 * status, whether this clinic performs the service and whether a session is
 * booked:
 *
 *   performed here:       REQUESTED → SCHEDULED → IN_PROGRESS → DONE
 *   performed elsewhere:  REQUESTED → DONE
 *   either:               CANCELLED
 *
 * Nothing here works it out again. When the BFF could not (an input it reads
 * was unavailable, or an older BFF), `stage` is null and the order is read from
 * its status instead — the kit's `orderStageOf` does the same, so a panel and
 * the visit room never name one order two ways.
 *
 * Colours are the kit's for the same stages (`orderStageColors`), derived from
 * swatches the installed framework already has; `variant` is the kit Badge's.
 * Labels come from `t('common.orderStage.<key>')` — never from this file.
 */
/**
 * What every read of an order selects about where it stands: «تاریخ انجام»
 * (YYYY-MM-DD; null for an order placed before the field), whether this clinic
 * performs it, the stage (null = read the status) and the soonest open booking.
 */
export const ORDER_STAGE_FIELDS = 'performedDate performedHere stage nextBookingAt';

export const ORDER_STAGES = ['REQUESTED', 'SCHEDULED', 'IN_PROGRESS', 'DONE', 'CANCELLED'];

const swatch = (s) => ({ bg: s.bg, text: s.text });
export const ORDER_STAGE_META = {
  REQUESTED: { key: 'requested', variant: 'warning', ...swatch(statusColors.waiting) },
  SCHEDULED: { key: 'scheduled', variant: 'outline', ...swatch(statusColors.scheduled) },
  IN_PROGRESS: { key: 'inProgress', variant: 'default', ...swatch(statusColors.in_visit) },
  DONE: { key: 'done', variant: 'success', ...swatch(statusColors.completed) },
  CANCELLED: { key: 'cancelled', variant: 'destructive', ...swatch(statusColors.cancelled) },
};

const STAGE_OF_STATUS = {
  pending: 'REQUESTED', in_progress: 'IN_PROGRESS', completed: 'DONE', cancelled: 'CANCELLED',
};

/** An order's stage: the BFF's, else read from its status; null for neither. */
export function orderStageOf(order) {
  const stage = order?.stage;
  if (stage && Object.prototype.hasOwnProperty.call(ORDER_STAGE_META, stage)) return stage;
  return STAGE_OF_STATUS[order?.status] || null;
}

/**
 * The soonest open booking (`nextBookingAt`, ISO-8601) — the stage's second
 * line, «نوبت: …». Only while one is still to come: a closed order has none.
 */
export function orderBookingOf(order) {
  const stage = orderStageOf(order);
  return (stage === 'SCHEDULED' || stage === 'IN_PROGRESS') && order?.nextBookingAt
    ? order.nextBookingAt
    : null;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real 'YYYY-MM-DD' — 2026-02-30 is not one. */
function isIsoDay(value) {
  const m = ISO_DAY.exec(String(value ?? ''));
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

// The clinical service writes `created_at` as UTC wall time with no designator
// («2026-10-25T08:00:00.123456»), which `Date.parse` would read on the browser's
// clock. Anything without one is therefore read as UTC.
function instantMs(value) {
  if (value == null || value === '') return NaN;
  const s = String(value).trim();
  if (/^\d+$/.test(s)) return Number(s);
  const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  return Date.parse(zoned ? s : `${s.replace(' ', 'T')}Z`);
}

/**
 * «تاریخ انجام», 'YYYY-MM-DD' on the clinic's calendar: the order's own
 * (`performedDate`), else — an order placed before the field — the day it was
 * written; '' when neither can be read.
 */
export function orderDayOf(order) {
  if (isIsoDay(order?.performedDate)) return order.performedDate;
  const ms = instantMs(order?.createdAt);
  return Number.isFinite(ms) ? isoDayOf(ms) : '';
}

/**
 * A 'YYYY-MM-DD' as the instant `formatDate` renders on that very day: the
 * clinic's noon of it, so no zone between here and the formatter moves it.
 */
export function orderDayInstant(isoDay) {
  return isIsoDay(isoDay) ? wallToMs(isoDay, 12 * 60) : null;
}

// ── One appointment, one invoice (PM-0618, docs/ONE_APPOINTMENT_ONE_INVOICE_PLAN.md §14-15) ──
//
// Since PM-0618 every order carries one ROW per session from the moment it is
// placed, each with its own status and the مراجعه (appointment) it sits in. The
// status is the BFF's (`OrderSession.status`); a row from an older BFF is read
// the way the BFF's own `sessionStatusOf` reads it — never as DONE for want of
// an outcome (critic D1).

export const SESSION_STATUSES = ['REQUESTED', 'SCHEDULED', 'IN_PROGRESS', 'DONE', 'NOT_DONE', 'CANCELLED', 'VOIDED'];

/** Swatches from the stage map: REQUESTED=waiting … history rows = the cancelled swatch. */
export const SESSION_STATUS_META = {
  REQUESTED: { key: 'requested', variant: 'warning', history: false, ...swatch(statusColors.waiting) },
  SCHEDULED: { key: 'scheduled', variant: 'outline', history: false, ...swatch(statusColors.scheduled) },
  IN_PROGRESS: { key: 'inProgress', variant: 'default', history: false, ...swatch(statusColors.in_visit) },
  DONE: { key: 'done', variant: 'success', history: false, ...swatch(statusColors.completed) },
  NOT_DONE: { key: 'notDone', variant: 'destructive', history: true, ...swatch(statusColors.cancelled) },
  CANCELLED: { key: 'cancelled', variant: 'destructive', history: true, ...swatch(statusColors.cancelled) },
  VOIDED: { key: 'voided', variant: 'destructive', history: true, ...swatch(statusColors.cancelled) },
};

/** A session's status: the BFF's, else read from an older row. Null for no row. */
export function sessionStatusOf(s) {
  if (!s) return null;
  const st = String(s.status || '').toUpperCase();
  if (SESSION_STATUS_META[st]) return st;
  if (s.voidedAt) return 'VOIDED';
  const o = String(s.outcome || '').toUpperCase();
  if (o === 'COMPLETED') return 'DONE';
  if (o === 'IN_PROGRESS') return 'IN_PROGRESS';
  return 'REQUESTED';
}

/** Live = not a history row. */
export const isLiveSession = (s) => {
  const st = sessionStatusOf(s);
  return !!st && !SESSION_STATUS_META[st].history;
};

/** «باز» — sitting in a مراجعه and not yet answered: SCHEDULED or IN_PROGRESS. */
export const isOpenSession = (s) => {
  const st = sessionStatusOf(s);
  return st === 'SCHEDULED' || st === 'IN_PROGRESS';
};

/** The session list's order: by number, then a history row before its replacement. */
export function sortedSessions(sessions) {
  const ms = (v) => { const n = Date.parse(v || ''); return Number.isFinite(n) ? n : 0; };
  return [...(sessions || [])].sort((a, b) => (a.sessionNo || 0) - (b.sessionNo || 0)
    || ms(a.createdAt) - ms(b.createdAt));
}

/** The lowest live REQUESTED session — what «جلسهٔ بعدی» books and «افزودن به این مراجعه» attaches. */
export function nextRequestedSession(sessions) {
  return sortedSessions(sessions).find((s) => sessionStatusOf(s) === 'REQUESTED') || null;
}

/**
 * May the desk say how this session ended — «انجام شد» / «انجام نشد» — here and
 * now (PM-0618)? Only while it is open (SCHEDULED / IN_PROGRESS) AND sits in a
 * مراجعه the patient is in now: `openVisitIds`, the BFF's patientOpenVisits —
 * open at this clinic on its today (arrived / in the visit, an earlier day's
 * never closed, or finished today with its invoice still waiting). A session
 * booked for a later day has not happened; one whose مراجعه has not begun waits
 * for the patient; one whose مراجعه has closed is answered at that invoice's
 * checkout («خدمات باز این مراجعه»).
 */
export function isAnswerableSession(session, openVisitIds = []) {
  return isOpenSession(session) && !!session.appointmentId
    && (openVisitIds instanceof Set ? openVisitIds : new Set(openVisitIds || [])).has(session.appointmentId);
}

/**
 * The open session «انجام شد» records when the desk did not pick one (PM-0618):
 * one the desk may answer now (isAnswerableSession) — the one under way first.
 * null = none: the next waiting session joins the open مراجعه instead.
 */
export function sessionToRecord(sessions, { openVisitIds = [] } = {}) {
  const here = new Set(openVisitIds || []);
  const open = sortedSessions(sessions).filter((s) => isAnswerableSession(s, here));
  return open.find((s) => sessionStatusOf(s) === 'IN_PROGRESS') || open[0] || null;
}

/** Does a live session of the order already sit in this مراجعه? One per مراجعه (contract §2.2). */
export const holdsSessionIn = (sessions, appointmentId) => !!appointmentId && (sessions || [])
  .some((s) => isLiveSession(s) && sessionStatusOf(s) !== 'REQUESTED' && s.appointmentId === appointmentId);

export const ORDER_DUES = ['TODAY', 'FUTURE', 'OVERDUE'];

/**
 * Which «اقدام‌های درمانی» filter a waiting order falls in — the BFF's `due`
 * (worked out on the clinic's calendar, contract §6.0 `dueOf`). An older BFF
 * sends none; the same rule is then read from the order's stage and
 * «تاریخ انجام» against the clinic's today. Only a REQUESTED order has one.
 */
export function orderDueOf(order, todayIso) {
  if (order && ORDER_DUES.includes(order.due)) return order.due;
  if (order && order.due === null && 'due' in order) return null;
  if (orderStageOf(order) !== 'REQUESTED' || !todayIso) return null;
  const day = orderDayOf(order);
  if (!day || day === todayIso) return 'TODAY';
  return day < todayIso ? 'OVERDUE' : 'FUTURE';
}
